import React, { useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  useWindowDimensions,
  Platform,
} from "react-native";
import { useLocation, useNavigate } from "react-router-dom";
import { headerPurple, primaryDark } from "../Constants/Colors";
import useAppStore from "../StateStore/ZustandStore";

const navItems = [
  { label: "Home", path: "/" },
  { label: "DoorStop", path: "/doorstop" },
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { width } = useWindowDimensions();
  const setModal = useAppStore((state) => state.setModal);
  const navigate = useNavigate();
  const location = useLocation();

  const isMobile = width < 768;
  const isTablet = width >= 768 && width < 1024;
  const isDesktop = width >= 1024;

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);
  const closeMenu = () => setIsMenuOpen(false);

  const goTo = (path) => {
    navigate(path);
    closeMenu();
  };

  const scrollToTestimonials = () => {
    const scroll = () => {
      const element = document.getElementById("testimonials");
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(scroll, 150);
    } else {
      scroll();
    }
    closeMenu();
  };

  return (
    <View style={styles.header}>
      <View
        style={[
          styles.headerContainer,
          {
            paddingHorizontal: isMobile ? 16 : isTablet ? 20 : 32,
            paddingVertical: isMobile ? 12 : 16,
          },
        ]}
      >
        {/* Logo */}
        <TouchableOpacity style={styles.logo} onPress={() => goTo("/")}>
          <View style={styles.logoText}>
            <Text
              style={[
                styles.instantText,
                {
                  fontSize: isMobile ? 22 : isTablet ? 26 : 28,
                },
              ]}
            >
              INSTANT
            </Text>
            <Text
              style={[
                styles.digitsText,
                {
                  fontSize: isMobile ? 22 : isTablet ? 26 : 28,
                },
              ]}
            >
              {" "}
              DIGITS
            </Text>
          </View>
        </TouchableOpacity>

        {/* Desktop Nav */}
        {isDesktop && (
          <View style={styles.nav}>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={[
                styles.navList,
                { flexDirection: "row", alignItems: "center" },
              ]}
            >
              {navItems.map((item) => (
                <TouchableOpacity
                  key={item.path}
                  style={styles.navItem}
                  onPress={() => goTo(item.path)}
                >
                  <Text
                    style={[
                      styles.navLink,
                      location.pathname === item.path && styles.navLinkActive,
                    ]}
                  >
                    {item.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        )}

        {/* Desktop Buttons */}
        {isDesktop && (
          <View style={styles.headerActions}>
            <TouchableOpacity style={styles.btnSecondary} onPress={scrollToTestimonials}>
              <Text style={styles.btnSecondaryText}>CLIENT PROOF</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.btnPrimary} onPress={() => setModal("ContactUs")}>
              <Text style={styles.btnPrimaryText}>Get Started</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Mobile Menu Toggle */}
        {!isDesktop && (
          <TouchableOpacity
            style={[styles.menuToggle, isMenuOpen && styles.menuToggleActive]}
            onPress={toggleMenu}
          >
            <View style={styles.menuLine} />
            <View style={styles.menuLine} />
            <View style={styles.menuLine} />
          </TouchableOpacity>
        )}
      </View>

      {/* Mobile Menu Dropdown */}
      {!isDesktop && isMenuOpen && (
        <View>
          <View style={styles.mobileMenu}>
            <View style={styles.mobileNavList}>
              {navItems.map((item) => (
                <TouchableOpacity
                  key={item.path}
                  style={styles.mobileNavItem}
                  onPress={() => goTo(item.path)}
                >
                  <Text
                    style={[
                      styles.mobileNavLink,
                      location.pathname === item.path && styles.navLinkActive,
                    ]}
                  >
                    {item.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            <View style={styles.mobileActions}>
              <TouchableOpacity style={styles.btnSecondary} onPress={scrollToTestimonials}>
                <Text style={styles.btnSecondaryText}>CLIENT PROOF</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.btnPrimary} onPress={() => setModal("ContactUs")}>
                <Text style={styles.btnPrimaryText}>Get Started</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}
    </View>
  );
};

const webExtras = {
  ...(Platform.OS === "web"
    ? {
        header: {
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 20px rgba(139, 92, 246, 0.25)",
          borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
        },
        mobileMenu: {
          boxShadow: "0 16px 40px rgba(15, 23, 42, 0.45)",
        },
      }
    : {}),
};

const styles = {
  header: {
    backgroundColor: primaryDark,
    position: "sticky",
    top: 0,
    zIndex: 9999,
    ...webExtras.header,
    width: "100%",
  },
  headerContainer: {
    maxWidth: 1200,
    marginHorizontal: "auto",
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    zIndex: 9999,
  },
  logo: {
    flexShrink: 0,
  },
  logoText: {
    flexDirection: "row",
    alignItems: "center",
  },
  instantText: {
    fontWeight: "700",
    color: "#FFFFFF",
    marginRight: 3,
    textShadowColor: "rgba(0,0,0,0.3)",
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  digitsText: {
    fontWeight: "700",
    color: "#FDE047",
    textShadowColor: "rgba(0,0,0,0.3)",
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  nav: {
    flex: 1,
    paddingHorizontal: 32,
    justifyContent: "center",
  },
  navList: {
    justifyContent: "center",
  },
  navItem: {
    marginHorizontal: 16,
    paddingVertical: 8,
  },
  navLink: {
    color: "#FFFFFF",
    fontSize: 15,
    fontWeight: "500",
    opacity: 0.9,
  },
  navLinkActive: {
    opacity: 1,
    fontWeight: "700",
    color: "#FDE047",
  },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  btnSecondary: {
    paddingHorizontal: 18,
    paddingVertical: 9,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.65)",
    backgroundColor: "transparent",
  },
  btnSecondaryText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "500",
  },
  btnPrimary: {
    paddingHorizontal: 22,
    paddingVertical: 10,
    borderRadius: 999,
    backgroundColor: "#FFFFFF",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 3,
  },
  btnPrimaryText: {
    color: headerPurple,
    fontSize: 14,
    fontWeight: "600",
  },
  menuToggle: {
    width: 36,
    height: 36,
    justifyContent: "center",
    alignItems: "center",
  },
  menuToggleActive: {
    backgroundColor: "rgba(255,255,255,0.15)",
    borderRadius: 999,
  },
  menuLine: {
    width: 22,
    height: 2,
    backgroundColor: "#FFFFFF",
    marginVertical: 2,
    borderRadius: 999,
  },
  mobileMenu: {
    backgroundColor: "rgba(76, 29, 149, 0.98)",
    paddingHorizontal: 16,
    paddingBottom: 16,
    position: "absolute",
    width: "100%",
    ...webExtras.mobileMenu,
  },
  mobileNavList: {
    paddingTop: 8,
  },
  mobileNavItem: {
    paddingVertical: 8,
  },
  mobileNavLink: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "500",
  },
  mobileActions: {
    marginTop: 12,
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 10,
  },
};

export default Header;
